import { useMemo, useState } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { Card, InsightSection, QueryState } from "./InsightSection";
import { useUnplayed } from "./useSteamInsights";

const PAGE_SIZE = 20;

const COLUMNS = [
    { key: "name", label: "Game", numeric: false },
    { key: "backlog_score", label: "Backlog score", numeric: true },
    { key: "metacritic_score", label: "Metacritic", numeric: true },
    { key: "n_achievements", label: "Achievements", numeric: true },
];

const compare = (key, direction) => (a, b) => {
    const av = a[key];
    const bv = b[key];
    // Missing values always sort last
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    const result = typeof av === "string" ? av.localeCompare(bv) : av - bv;
    return direction === "asc" ? result : -result;
};

export const UnplayedTable = () => {
    const query = useUnplayed();
    const [sort, setSort] = useState({ key: "backlog_score", direction: "desc" });
    const [showAll, setShowAll] = useState(false);

    const rows = useMemo(
        () => [...(query.data || [])].sort(compare(sort.key, sort.direction)),
        [query.data, sort]
    );
    const visible = showAll ? rows : rows.slice(0, PAGE_SIZE);

    const toggleSort = (key, numeric) =>
        setSort((current) =>
            current.key === key
                ? { key, direction: current.direction === "asc" ? "desc" : "asc" }
                : { key, direction: numeric ? "desc" : "asc" }
        );

    return (
        <InsightSection
            id="unplayed"
            title="Never played"
            intro={
                query.data
                    ? `${query.data.length.toLocaleString("en-GB")} library games have no playtime and no achievements. The backlog score is Metacritic (or 50 when missing) multiplied by how much you play the game's genres.`
                    : "Library games with no playtime and no achievements."
            }
        >
            <Card className="p-0 sm:p-0">
                <QueryState query={query} isEmpty={!rows.length} emptyText="Every library game has been played.">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-gray-50">
                                <tr>
                                    {COLUMNS.map((column) => {
                                        const active = sort.key === column.key;
                                        const Icon = active && sort.direction === "asc" ? ChevronUpIcon : ChevronDownIcon;
                                        return (
                                            <th
                                                key={column.key}
                                                scope="col"
                                                aria-sort={active ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}
                                                className={`whitespace-nowrap px-4 py-2 font-semibold text-gray-700 ${column.numeric ? "text-right" : "text-left"}`}
                                            >
                                                <button
                                                    type="button"
                                                    onClick={() => toggleSort(column.key, column.numeric)}
                                                    className="inline-flex min-h-[44px] items-center gap-1 hover:text-gray-900"
                                                >
                                                    {column.label}
                                                    <Icon className={`h-4 w-4 ${active ? "text-gray-700" : "text-gray-300"}`} />
                                                </button>
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                                {visible.map((game) => (
                                    <tr key={game.appid}>
                                        <td className="px-4 py-2 text-gray-800">
                                            <div>{game.name || `App ${game.appid}`}</div>
                                            {game.genres?.length > 0 && (
                                                <div className="text-sm text-gray-500">{game.genres.join(", ")}</div>
                                            )}
                                        </td>
                                        <td className="px-4 py-2 text-right font-mono text-gray-700">
                                            {game.backlog_score != null ? game.backlog_score.toFixed(2) : "–"}
                                        </td>
                                        <td className="px-4 py-2 text-right font-mono text-gray-700">{game.metacritic_score ?? "–"}</td>
                                        <td className="px-4 py-2 text-right font-mono text-gray-700">{game.n_achievements || "–"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {rows.length > PAGE_SIZE && (
                        <div className="border-t border-gray-200 p-2 text-center">
                            <button
                                type="button"
                                onClick={() => setShowAll((value) => !value)}
                                className="min-h-[44px] px-4 text-sm font-semibold text-gray-700 underline decoration-gray-300 underline-offset-4 hover:text-gray-900"
                            >
                                {showAll ? "Show fewer" : `Show all ${rows.length.toLocaleString("en-GB")}`}
                            </button>
                        </div>
                    )}
                </QueryState>
            </Card>
        </InsightSection>
    );
};
